import React, { useContext, useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../../components/FormInput";
import { validationRules } from "../../../utils/validationRules";
import {
  createBusinessLoan,
  getBusinessLoanByEmail,
  getBusinessLoanByMobile,
  getAllCities,
  getAllPincode,
  getAllStates,
  getBusinessLoanBankList,
  createOrGetCustomer
} from "../../../middleware/backend_hepler";
import { AuthContext } from "../../../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Stepper, Step, StepLabel } from "@mui/material";
import { Row, Col, Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SelectInput from "../../../components/SelectInput";
import DropDownInput from "../../../components/DropDownInput";
import { FaTrashAlt } from "react-icons/fa";


const CreateBusinessForm = () => {
  const { adminId, adminName, isLoggedIn, adminRole } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [pincodenumber, setPincode] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [mobileExists, setMobileExists] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [presentState, setPresentState] = useState(null);
  const [presentCityOptions, setPresentCityOptions] = useState([]);
  const [presentPincodeOptions, setPresentPincodeOptions] = useState([]);
  const [businessLoanBanks, setBusinessLoanBanks] = useState([]);
  const [files, setFiles] = useState({});

  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
    watch,
  } = methods;
  const email = watch("email");
  const mobile = watch("mobile");
  const dob = watch('dob');
  const maritalStatus = watch("marital_status");
  const coApplicantRelationships = watch("co_applicant_relationship");

  const [emailExists, setEmailExists] = useState(false);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const fileRefs = {
    pan: useRef(null),
    aadhar: useRef(null),
    photo: useRef(null),
    bank_account: useRef(null),
    itr: useRef(null),
    gst: useRef(null),
    msme: useRef(null),
    others: useRef(null),
    co_applicant_pan: useRef(null),
    co_applicant_aadhar: useRef(null),
    co_applicant_photo: useRef(null),
  };

  const steps = ["Personal Details", "Income Details", "Upload Documents"];

  const handleStepChange = async (newStep) => {
    if (newStep > activeStep) {
      const isValid = await trigger();
      if (isValid) {
        if (emailExists) {
          toast.error("Email already exists. Please use a different email.");
          return;
        }

        if (mobileExists) {
          toast.error(
            "Mobile number already exists. Please use a different mobile number."
          );
          return;
        }

        setActiveStep(newStep);
      }
    } else {
      setActiveStep(newStep);
    }
  };

  const checkMobile = async (mobile) => {
    if (mobile) {
      try {
        const data = await getBusinessLoanByMobile(mobile);
        setMobileExists(Boolean(data));
      } catch (error) {
        setMobileExists(false);
      }
    }
  };

  useEffect(() => {
    const fetchBusinessLoanBanks = async () => {
      try {
        const response = await getBusinessLoanBankList();
        const banksData = response.data.map(bank => ({
          value: bank.bank_name,
          label: bank.bank_name
        }));
        banksData.sort((a, b) => a.label.localeCompare(b.label));
        setBusinessLoanBanks(banksData);
      } catch (error) {
        console.error("Error fetching business loan banks:", error);
      }
    };
    fetchBusinessLoanBanks();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesData = await getAllStates();
        const availableStates = statesData.filter(
          (state) => state.availability === "Yes"
        );
        setStates(availableStates);
        setFilteredStates(availableStates);
      } catch (error) {
        console.error("Error fetching states data:", error);
      }
    };
    fetchStates();
  }, []);

  const stateOptions = filteredStates.map((state) => ({
    value: state.name,
    label: state.name,
  }));

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesData = await getAllCities();
        citiesData.sort((a, b) => a.name.localeCompare(b.name));
        setCities(citiesData);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchPinCode = async () => {
      try {
        const pincodeData = await getAllPincode();
        setPincode(pincodeData);
      } catch (error) {
        console.error("Error fetching pincodes:", error);
      }
    };
    fetchPinCode();
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setCityOptions([]);
      setPincodeOptions([]);
      return;
    }

    const filteredCities = cities.filter(
      (city) => city.State_name === selectedState
    );

    const cityOptions = filteredCities.map((city) => ({
      value: city.name,
      label: city.name,
    }));

    setCityOptions(cityOptions);

    const filteredPincodes = pincodenumber.filter(
      (pincode) => pincode.state === selectedState.toUpperCase()
    );

    const uniquePincodes = new Set();
    const uniqueOptions = [];

    filteredPincodes.forEach((pincode) => {
      if (!uniquePincodes.has(pincode.pincode)) {
        uniquePincodes.add(pincode.pincode);
        uniqueOptions.push({
          value: pincode.pincode,
          label: pincode.pincode,
        });
      }
    });

    uniqueOptions.sort((a, b) => a.value - b.value);
    setPincodeOptions(uniqueOptions);

    if (!presentState) {
      setPresentCityOptions([]);
      setPresentPincodeOptions([]);
    } else {
      const filteredCities = cities.filter(
        (city) => city.State_name === presentState
      );

      const presentCityOptions = filteredCities.map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setPresentCityOptions(presentCityOptions);

      const filteredPincodes = pincodenumber.filter(
        (pincode) => pincode.state === presentState.toUpperCase()
      );

      const uniquePincodes = new Set();
      const uniqueOptions = [];
      filteredPincodes.forEach((pincode) => {
        if (!uniquePincodes.has(pincode.pincode)) {
          uniquePincodes.add(pincode.pincode);
          uniqueOptions.push({
            value: pincode.pincode,
            label: pincode.pincode,
          });
        }
      });

      uniqueOptions.sort((a, b) => a.value - b.value);
      setPresentPincodeOptions(uniqueOptions);
    }
  }, [selectedState, presentState, cities, pincodenumber]);

  const checkEmail = async (email) => {
    if (email) {
      try {
        const data = await getBusinessLoanByEmail(email);
        setEmailExists(Boolean(data));
      } catch (error) {
        setEmailExists(false);
      }
    }
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    if (files.length > 0) {
      setFiles((prev) => ({
        ...prev,
        [name]: files.length > 0 ? files[0] : null,
      }));
    }
  };

  const handleClearFile = (name) => {
    if (fileRefs[name] && fileRefs[name].current) {
      fileRefs[name].current.value = null;
      setFiles((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption?.value || null);
    setValue("state", selectedOption?.value || "");
  };

  const getSelectedStateValue = () => {
    return stateOptions.find((option) => option.value === selectedState);
  };

  const handlePresentStateChange = (selectedOption) => {
    setPresentState(selectedOption?.value || null);
    setValue("current_state", selectedOption?.value || "");
  };

  const getSelectedPresentStateValue = () => {
    return stateOptions.find((option) => option.value === presentState);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const mobile = localStorage.getItem("verifiedMobile");
    const name = data.name;
    try {
      if (!isLoggedIn) {
        // Not logged in: create or get customer
        let adminId;
        let adminName = name;

        try {
          const customerResponse = await createOrGetCustomer({ mobile, name });
          adminId = customerResponse.adminId;
          adminName = customerResponse.name || name;
          // toast.info(customerResponse.isNew ? "Customer created successfully!" : "Customer already exists!");
        } catch (err) {
          throw err;
        }

        // Set created_by and created_id for the home loan
        data.created_by = adminName;
        data.created_id = adminId;
        data.status = data.status || "open";
        data.assigned_to = data.assigned_to || "";

      } else {
        data.created_by = adminName;
        data.created_id = adminId;
      }

      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (
          key === "pan" ||
          key === "aadhar" ||
          key === "photo" ||
          key === "bank_account" ||
          key === "itr" ||
          key === "gst" ||
          key === "msme" ||
          key === "others" ||
          key === "co_applicant_photo" ||
          key === "co_applicant_pan" ||
          key === "co_applicant_aadhar"
        ) {
          formData.append(key, data[key][0]);
        } else {
          formData.append(key, data[key]);
        }
      });

      const response = await createBusinessLoan(formData);
      toast.success("Business loan created successfully!");
      setTimeout(() => {
        navigate(isLoggedIn ? "/businessloanlist" : "/");
      }, 2000);

    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.message;
      console.error("Error submitting business loan:", errorMessage || error);
      toast.error("Failed to submit loan application. Please try again.");
    }
  };

  React.useEffect(() => {
    if (email) {
      checkEmail(email);
    }
  }, [email]);

  React.useEffect(() => {
    if (mobile) {
      checkMobile(mobile);
    }
  }, [mobile]);

  React.useEffect(() => {
    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();

      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age >= 0) {
        setValue('age', age);
      } else {
        setValue('age', '');
      }
    }
  }, [dob, setValue]);

  const handleGoBack = () => {
    if (isLoggedIn) {
      navigate("/businessloanlist");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="container">
      {activeStep === 0 && (
        <div className="edit-back-icon">
          <div className="edit-back-text" onClick={handleGoBack}>
            &lt; Go Back
          </div>
        </div>
      )}
      <h2 className="my-4">Business Loan</h2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="form-group">
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            className="stepper-container"
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <div className="form-content">
            {activeStep === 0 && (
              <div className="sections personal-details-sections">
                <h4>Personal Details</h4>
                <Row>
                  <Col md={6}>
                    <FormInput
                      name="name"
                      label="Name"
                      registerOptions={validationRules.name}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="dob"
                      label="Date of Birth"
                      type="date"
                      registerOptions={{
                        required: "Date of Birth is required",
                        validate: {
                          isOldEnough: (value) => {
                            const today = new Date();
                            const minAgeDate = new Date();
                            const maxAgeDate = new Date();
                            minAgeDate.setFullYear(today.getFullYear() - 21);
                            maxAgeDate.setFullYear(today.getFullYear() - 58);
                            return (
                              (new Date(value) <= minAgeDate &&
                                new Date(value) >= maxAgeDate) ||
                              "Choose age from 21 to 58"
                            );
                          },
                          isNotFutureDate: (value) => {
                            return (
                              new Date(value) <= new Date() ||
                              "Date of Birth cannot be in the future"
                            );
                          },
                        },
                      }}
                      error={errors.dob?.message}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormInput name="age" label="Age" type="number" readOnly />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="father_name"
                      label="Father Name"
                      registerOptions={validationRules.father_name}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="mother_name"
                      label="Mother Name"
                      registerOptions={validationRules.mother_name}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="mobile"
                      label="Mobile"
                      type="tel"
                      registerOptions={validationRules.mobile}
                      error={
                        mobileExists
                          ? "Mobile number already exists"
                          : errors.mobile?.message
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="email"
                      label="Email ID"
                      registerOptions={validationRules.email}
                    />
                  </Col>
                  <Col md={6}>
                    <DropDownInput
                      name="marital_status"
                      label="Marital Status"
                      options={[
                        { value: "single", label: "Single" },
                        { value: "married", label: "Married" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  {maritalStatus === "Married" && (
                    <Col md={6}>
                      <FormInput
                        name="spouse_name"
                        label="Spouse Name"
                        registerOptions={{
                          required: "Spouse Name is required",
                        }}
                      />
                    </Col>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="residence_type"
                      label="Residence Type"
                      options={[
                        { value: "Own", label: "Own" },
                        { value: "Rent", label: "Rent" },
                        {
                          value: "Owned By Me / Spouse",
                          label: "Owned By Me / Spouse",
                        },
                        {
                          value: "Owned By My / Parents",
                          label: "Owned By My / Parents",
                        },
                      ]}
                      registerOptions={{
                        required: "Residence type is required",
                      }}
                      error={errors.residence_type?.message}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="location"
                      label="Present Staying Address"
                      registerOptions={{ required: "Address is required" }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="landmark"
                      label="Landmark"
                      registerOptions={{ required: "Landmark is required" }}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      name="state"
                      label="State"
                      options={stateOptions}
                      value={getSelectedStateValue()}
                      registerOptions={{ required: "State is required" }}
                      onChange={handleStateChange}
                      error={errors.state?.message}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      name="city"
                      label="City"
                      options={cityOptions}
                      registerOptions={{ required: "City is required" }}
                      error={errors.city?.message}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      name="pincode"
                      label="Pincode"
                      options={pincodeOptions}
                      registerOptions={{ required: "Pincode is required" }}
                    />
                  </Col>
                  <Col md={6}>
                    <DropDownInput
                      name="present_address_adhaar"
                      label="Is your Present Address as per Aadhaar"
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                      registerOptions={{
                        required: "This selection is required",
                      }}
                      error={errors.present_address_adhaar?.message}
                    />
                  </Col>
                  {watch("present_address_adhaar") === "No" && (
                    <>
                      <Col md={6}>
                        <FormInput
                          name="current_location"
                          label="Present Aadhar Address"
                          registerOptions={{
                            required: "Address is required",
                          }}
                          error={errors.current_location?.message}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          name="current_state"
                          label="State"
                          options={stateOptions}
                          value={getSelectedPresentStateValue()}
                          onChange={handlePresentStateChange}
                          registerOptions={{ required: "State is required" }}
                          error={errors.current_state?.message}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          name="current_city"
                          label="City"
                          options={presentCityOptions}
                          registerOptions={{ required: "City is required" }}
                          error={errors.current_city?.message}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          name="current_pincode"
                          label="Pincode"
                          options={presentPincodeOptions}
                          registerOptions={{ required: "Pincode is required" }}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          name="current_landmark"
                          label="Present Landmark"
                          registerOptions={{ required: "Landmark is required" }}
                          error={errors.current_landmark?.message}
                        />
                      </Col>
                    </>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="add_co_applicant"
                      label="Add Co-Applicant"
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                      registerOptions={{
                        required: "Selection is required",
                      }}
                    />
                  </Col>
                </Row>

                {watch("add_co_applicant") === "Yes" && (
                  <div className="sections co-applicant-details-sections">
                    <h4>Co-Applicant Details</h4>
                    <Row>
                      <Col md={6}>
                        <DropDownInput
                          name="co_applicant_relationship"
                          label="Relationship with Applicant"
                          options={[
                            { value: "spouse", label: "Spouse" },
                            { value: "father", label: "Father" },
                            { value: "mother", label: "Mother" },
                            { value: "son", label: "Son" },
                            { value: "daughter", label: "Daughter" },
                          ]}
                          registerOptions={{
                            required: "Relationship is required",
                          }}
                        />
                      </Col>
                      {coApplicantRelationships === "spouse" && (
                        <Col md={6}>
                          <FormInput
                            name="co_applicant_spouse_name"
                            label="Co-Applicant Spouse Name"
                            registerOptions={{
                              required: "Co-Applicant Spouse Name is required",
                            }}
                          />
                        </Col>
                      )}
                      {coApplicantRelationships === "father" && (
                        <Col md={6}>
                          <FormInput
                            name="co_applicant_fathers_name"
                            label="Co-Applicant Father Name"
                            registerOptions={{
                              required: "Co-Applicant Father Name is required",
                            }}
                          />
                        </Col>
                      )}
                      {coApplicantRelationships === "mother" && (
                        <Col md={6}>
                          <FormInput
                            name="co_applicant_mothers_name"
                            label="Co-Applicant Mother Name"
                            registerOptions={{
                              required: "Co-Applicant Mother Name is required",
                            }}
                          />
                        </Col>
                      )}
                      {coApplicantRelationships === "daughter" && (
                        <Col md={6}>
                          <FormInput
                            name="co_applicant_daughter_name"
                            label="Co-Applicant Daughter Name"
                            registerOptions={{
                              required:
                                "Co-Applicant Daughter Name is required",
                            }}
                          />
                        </Col>
                      )}
                      {coApplicantRelationships === "son" && (
                        <Col md={6}>
                          <FormInput
                            name="co_applicant_son_name"
                            label="Co-Applicant Son Name"
                            registerOptions={{
                              required: "Co-Applicant Son Name is required",
                            }}
                          />
                        </Col>
                      )}
                      <Col md={6}>
                        <FormInput
                          name="co_applicant_mobile"
                          label="Co-Applicant Mobile"
                          type="tel"
                          registerOptions={{
                            required: "Co-Applicant Mobile is required",
                          }}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          name="co_applicant_email"
                          label="Co-Applicant Email"
                          type="email"
                          registerOptions={{
                            required: "Co-Applicant Email is required",
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            )}

            {activeStep === 1 && (
              <div className="sections loan-details-sections">
                <h4>Income Details</h4>
                <Row>
                  <Col md={6}>
                    <FormInput
                      name="required_loan_amount"
                      label="Required Loan Amount"
                      type="number"
                      registerOptions={validationRules.loanAmount}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="turnover"
                      label="Annual Turn Over"
                      type="number"
                      registerOptions={validationRules.turnover}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="existing_emi_amount"
                      label="Existing Total EMI"
                      type="number"
                    />
                  </Col>
                  {isLoggedIn && adminRole !==  "customer" &&(
                    <Col md={6}>
                      <SelectInput
                        name="bank_name"
                        label="Bank Name"
                        options={businessLoanBanks}
                      />
                    </Col>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="company_type"
                      label="Your Company Registered As"
                      options={[
                        { value: "Private Limited", label: "Private Limited" },
                        { value: "Proprietorship", label: "Proprietorship" },
                        { value: "LLP", label: "LLP" },
                        { value: "Not Registered", label: "Not Registered" },
                      ]}
                    />
                  </Col>
                  <Col md={6}>
                    <DropDownInput
                      name="business_type"
                      label="Nature of Business"
                      options={[
                        { value: "Manufacturing", label: "Manufacturing" },
                        { value: "Retail", label: "Retail" },
                        { value: "Dealer", label: "Dealer" },
                        { value: "Distributer", label: "Distributer" },
                        { value: "Wholesale", label: "Wholesale" },
                        { value: "Service", label: "Service" },
                        { value: "Agriculture", label: "Agriculture" },
                        { value: "Real Estate", label: "Real Estate" },
                        { value: "Construction", label: "Construction" },
                        { value: "Builder", label: "Builder" },
                        { value: "E-Commerce", label: "E-Commerce" },
                        { value: "Transport", label: "Transport" },
                        { value: "Others", label: "Others" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  {watch("business_type") === "Others" && (
                    <Col md={6}>
                      <FormInput
                        name="description"
                        label="Others"
                        registerOptions={{
                          required: "Others is required",
                        }}
                      />
                    </Col>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="business_property"
                      label="Business Place"
                      options={[
                        { value: "Own", label: "Own" },
                        { value: "Rent", label: "Rent" },
                        { value: "lease", label: "Lease" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  {watch("business_property") === "Own" && (
                    <Col md={6}>
                      <FormInput
                        name="business_year"
                        label="Business Start Year"
                        type="number"
                        registerOptions={{
                          required: "Business Start Year is required",
                          validate: (value) => {
                            const enteredYear = parseInt(value, 10);
                            const currentYear = new Date().getFullYear();
                            if (isNaN(enteredYear)) {
                              return "Please enter a valid year";
                            }
                            if (enteredYear > currentYear) {
                              return `Future dates are not allowed`;
                            }
                            return true;
                          },
                        }}
                      />
                    </Col>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="gst_registered"
                      label="GST Registered"
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  {watch("gst_registered") === "Yes" && (
                    <Col md={6}>
                      <FormInput
                        name="gst_registerd_year"
                        label="GST Registered Year"
                        type="number"
                        registerOptions={{
                          required: "GST Registered Year is required",
                          validate: (value) => {
                            const enteredYear = parseInt(value, 10);
                            const currentYear = new Date().getFullYear();
                            if (isNaN(enteredYear)) {
                              return "Please enter a valid year";
                            }
                            if (enteredYear > currentYear) {
                              return `Future dates are not allowed`;
                            }
                            return true;
                          },
                        }}
                      />
                    </Col>
                  )}
                  <Col md={6}>
                    <DropDownInput
                      name="msme_registered"
                      label="MSME Registered"
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  <Col md={6}>
                    <DropDownInput
                      name="filling_itr"
                      label="Are You Filling ITR"
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                    />
                  </Col>
                  <Col md={6}>
                    <DropDownInput
                      name="add_reference"
                      label="Add Reference"
                      options={[
                        { value: "Buyer / Seller", label: "Buyer / Seller" },
                        { value: "Friend / Relative", label: "Friend / Relative" },
                      ]}
                    />
                  </Col>
                </Row>
                <Row>
                  <h4>Reference Details</h4>
                  <Col md={6}>
                    <FormInput name="reference_name" label="Name" />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      name="reference_mobile"
                      label="Mobile"
                      type="tel"
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput name="reference_address" label="Address" />
                  </Col>
                  <Col md={6}>
                    <FormInput name="reference_location" label="Location" />
                  </Col>
                </Row>
              </div>
            )}
            {activeStep === 2 && (
              <div className="sections financial-details-sections">
                <h4>Please Upload Clear Copy</h4>
                <div className="form-row">
                  <div className="sections document-upload-sections">
                    <div className="document-upload-group create-document">
                      <label
                        htmlFor="photo"
                        className="document-label required"
                      >
                        Photo <br /> (Selfie Recommended)
                      </label>
                      <div className="document-file-container">
                        <input
                          type="file"
                          id="photo"
                          name="photo"
                          className="form-control-file"
                          {...methods.register("photo", {
                            required: "Photo is required",
                            onChange: (e) => handleFileChange(e), // merge your custom onChange
                          })}
                          ref={(e) => {
                            methods.register("photo").ref(e); // react-hook-form ref
                            fileRefs.photo.current = e;
                          }}
                        />
                        {files.photo && (
                          <FaTrashAlt
                            className="trash-icon"
                            title="Remove file"
                            onClick={() => handleClearFile("photo")}
                          />
                        )}
                      </div>
                      {errors.photo && (
                        <span className="error-message">
                          {errors.photo.message}
                        </span>
                      )}
                    </div>

                    <div className="document-upload-group create-document">
                      <label
                        htmlFor="aadhar"
                        className="document-label required"
                      >
                        Aadhaar /eAadhaar
                      </label>
                      <div className="document-file-container">
                        <input
                          type="file"
                          id="aadhar"
                          name="aadhar"
                          className="form-control-file"
                          {...methods.register("aadhar", {
                            required: "Aadhaar Card is required",
                            onChange: (e) => handleFileChange(e),
                          })}
                          ref={(e) => {
                            methods.register("aadhar").ref(e);
                            fileRefs.aadhar.current = e;
                          }}
                        />
                        {files.aadhar && (
                          <FaTrashAlt
                            className="trash-icon"
                            title="Remove file"
                            onClick={() => handleClearFile("aadhar")}
                          />
                        )}
                      </div>
                      {errors.aadhar && (
                        <span className="error-message">
                          {errors.aadhar.message}
                        </span>
                      )}
                    </div>

                    <div className="document-upload-group create-document">
                      <label htmlFor="pan" className="document-label required">
                        PAN Card
                      </label>
                      <div className="document-file-container">
                        <input
                          type="file"
                          id="pan"
                          name="pan"
                          className="form-control-file"
                          {...methods.register("pan", {
                            required: "PAN Card is required",
                            onChange: (e) => handleFileChange(e),
                          })}
                          ref={(e) => {
                            methods.register("pan").ref(e);
                            fileRefs.pan.current = e;
                          }}
                        />
                        {files.pan && (
                          <FaTrashAlt
                            className="trash-icon"
                            title="Remove file"
                            onClick={() => handleClearFile("pan")}
                          />
                        )}
                      </div>
                      {errors.pan && (
                        <span className="error-message">
                          {errors.pan.message}
                        </span>
                      )}
                    </div>

                    {watch("gst_registered") === "Yes" && (
                      <div className="document-upload-group create-document">
                        <label
                          htmlFor="gst"
                          className="document-label required"
                        >
                          GST Certificate
                        </label>
                        <div className="document-file-container">
                          <input
                            type="file"
                            id="gst"
                            name="gst"
                            className="form-control-file"
                            {...methods.register("gst", {
                              required: "GST is required",
                              onChange: (e) => handleFileChange(e),
                            })}
                            ref={(e) => {
                              methods.register("gst").ref(e);
                              fileRefs.gst.current = e;
                            }}
                          />
                          {files.gst && (
                            <FaTrashAlt
                              className="trash-icon"
                              title="Remove file"
                              onClick={() => handleClearFile("gst")}
                            />
                          )}
                        </div>
                        {errors.gst && (
                          <span className="error-message">
                            {errors.gst.message}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="document-upload-group create-document">
                      <label
                        htmlFor="bank_account"
                        className="document-label required"
                      >
                         CA/OD/CC Account Statement <br /> (Last 12 Months)
                      </label>
                      <div className="document-file-container">
                        <input
                          type="file"
                          id="bank_account"
                          name="bank_account"
                          className="form-control-file"
                          accept=".pdf"
                          {...methods.register("bank_account", {
                            required: "Bank Statement is required",
                            onChange: (e) => handleFileChange(e),
                          })}
                          ref={(e) => {
                            methods.register("bank_account").ref(e);
                            fileRefs.bank_account.current = e;
                          }}
                        />
                        {files.bank_account && (
                          <FaTrashAlt
                            className="trash-icon"
                            title="Remove file"
                            onClick={() => handleClearFile("bank_account")}
                          />
                        )}
                      </div>
                      <span className="file-upload-message">
                        Upload only PDF files & without password
                      </span>
                      {errors.bank_account && (
                        <span className="error-message">
                          {errors.bank_account.message}
                        </span>
                      )}
                    </div>

                     {watch("gst_registered") === "Yes"&& (
                      <div className="document-upload-group create-document">
                        <label htmlFor="msme" className="document-label">
                          GST 3B <br /> (Last 18 Months)
                        </label>
                        <div className="document-file-container">
                        <input
                          type="file"
                          id="msme"
                          name="msme"
                          className="form-control-file"
                          {...methods.register("msme", {
                            onChange: (e) => handleFileChange(e),
                          })}
                          ref={(e) => {
                            methods.register("msme").ref(e);
                            fileRefs.msme.current = e;
                          }}
                        />
                        {files.msme && (
                          <FaTrashAlt
                            className="trash-icon"
                            title="Remove file"
                            onClick={() => handleClearFile("msme")}
                          />
                        )}
                        </div>
                      </div>
                    )}

                    {watch("filling_itr") === "Yes" && (
                      <div className="document-upload-group create-document">
                        <label
                          htmlFor="itr"
                          className="document-label required"
                        >
                           ITR Last 2 Years
                        </label>
                        <div className="document-file-container">
                          <input
                            type="file"
                            id="itr"
                            name="itr"
                            className="form-control-file"
                            {...methods.register("itr", {
                              required: "ITR is required",
                              onChange: (e) => handleFileChange(e),
                            })}
                            ref={(e) => {
                              methods.register("itr").ref(e);
                              fileRefs.itr.current = e;
                            }}
                          />
                          {files.itr && (
                            <FaTrashAlt
                              className="trash-icon"
                              title="Remove file"
                              onClick={() => handleClearFile("itr")}
                            />
                          )}
                        </div>
                        {errors.itr && (
                          <span className="error-message">
                            {errors.itr.message}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="document-upload-group create-document">
                      <label htmlFor="others" className="document-label">
                        Others
                      </label>
                      <div className="document-file-container">
                      <input
                        type="file"
                        id="others"
                        name="others"
                        className="form-control-file"
                        {...methods.register("others", {
                          onChange: (e) => handleFileChange(e),
                        })}
                        ref={(e) => {
                          methods.register("others").ref(e);
                          fileRefs.others.current = e;
                        }}
                      />
                      {files.others && (
                        <FaTrashAlt
                          className="trash-icon"
                          title="Remove file"
                          onClick={() => handleClearFile("others")}
                        />
                      )}
                      </div>
                    </div>
                  </div>
                </div>

                {watch("add_co_applicant") === "Yes" && (
                  <>
                    <h4 className="mt-4">ADD Co Applicant KYC</h4>
                    <div className="sections document-upload-sections">
                      <div className="document-upload-group create-document">
                        <div className="document-label-container">
                          <label
                            htmlFor="co_applicant_photo"
                            className="document-label required"
                          >
                            Co Applicant Photo (Selfie Recommended)
                          </label>
                        </div>
                        <div className="document-file-container">
                          <input
                            type="file"
                            id="co_applicant_photo"
                            name="co_applicant_photo"
                            className="form-control-file"
                            {...methods.register("co_applicant_photo", {
                              required: "Co Applicant Photo is required",
                              onChange: (e) => handleFileChange(e),
                            })}
                            ref={(e) => {
                              methods.register("co_applicant_photo").ref(e);
                              fileRefs.co_applicant_photo.current = e;
                            }}
                          />
                          {files.co_applicant_photo && (
                            <FaTrashAlt
                              className="trash-icon"
                              title="Remove file"
                              onClick={() => handleClearFile("co_applicant_photo")}
                            />
                          )}
                        </div>
                        {errors.co_applicant_photo && (
                          <span className="error-message">
                            {errors.co_applicant_photo.message}
                          </span>
                        )}
                      </div>

                      <div className="document-upload-group create-document">
                        <div className="document-label-container">
                          <label
                            htmlFor="co_applicant_pan"
                            className="document-label required"
                          >
                            Co Applicant PAN
                          </label>
                        </div>
                        <div className="document-file-container">
                          <input
                            type="file"
                            id="co_applicant_pan"
                            name="co_applicant_pan"
                            className="form-control-file"
                            {...methods.register("co_applicant_pan", {
                              required: "Co Applicant PAN is required",
                              onChange: (e) => handleFileChange(e),
                            })}
                            ref={(e) => {
                              methods.register("co_applicant_pan").ref(e);
                              fileRefs.co_applicant_pan.current = e;
                            }}
                          />
                          {files.co_applicant_pan && (
                            <FaTrashAlt
                              className="trash-icon"
                              title="Remove file"
                              onClick={() => handleClearFile("co_applicant_pan")}
                            />
                          )}
                        </div>
                        {errors.co_applicant_pan && (
                          <span className="error-message">
                            {errors.co_applicant_pan.message}
                          </span>
                        )}
                      </div>

                      <div className="document-upload-group create-document">
                        <div className="document-label-container">
                          <label
                            htmlFor="co_applicant_aadhar"
                            className="document-label required"
                          >
                            Co Applicant Aadhaar
                          </label>
                        </div>
                        <div className="document-file-container">
                          <input
                            type="file"
                            id="co_applicant_aadhar"
                            name="co_applicant_aadhar"
                            className="form-control-file"
                            {...methods.register("co_applicant_aadhar", {
                              required: "Co Applicant Aadhaar is required",
                              onChange: (e) => handleFileChange(e),
                            })}
                            ref={(e) => {
                              methods.register("co_applicant_aadhar").ref(e);
                              fileRefs.co_applicant_aadhar.current = e;
                            }}
                          />
                          {files.co_applicant_aadhar && (
                            <FaTrashAlt
                              className="trash-icon"
                              title="Remove file"
                              onClick={() => handleClearFile("co_applicant_aadhar")}
                            />
                          )}
                        </div>
                        {errors.co_applicant_aadhar && (
                          <span className="error-message">
                            {errors.co_applicant_aadhar.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="button-container">
            <Row className="mt-4">
              <Col className="text-end">
                {activeStep > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => handleStepChange(activeStep - 1)}
                    className="custom-back-button"
                  >
                    Go Back
                  </Button>
                )}
                {activeStep < steps.length - 1 && (
                  <Button
                    variant="primary"
                    onClick={() => handleStepChange(activeStep + 1)}
                    className="custom-next-button"
                  >
                    Next Step
                  </Button>
                )}
                {activeStep === steps.length - 1 && (
                  <Button
                    type="submit"
                    variant="primary"
                    className="custom-submit-button"
                  >
                    {loading ? "Processing..." : "Submit"}
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </form>
      </FormProvider>
      <ToastContainer />
    </div>
  );
};

export default CreateBusinessForm;
